/*
 * zeroLabs: Kaufen-Button, Szene 18 "Setzt auf" (docs/ideen.md, Abschnitt "In den Warenkorb").
 * Entschieden von Moritz am 04.10.2026 im Labor. Siehe docs/anpassungen.md, Abschnitt "Kaufen-Button".
 *
 * Reine Darstellung. Das Skript liest nur, was das Theme ohnehin tut, und verändert davon nichts:
 * - Das Theme setzt .btn--loading auf den Button, sobald es in den Warenkorb legt
 *   -> der Button zieht sich zum Kreis zusammen, ein Bogen läuft.
 * - Das Theme meldet Erfolg mit ajaxProduct:added am Formular (detail.addToCartBtn)
 *   -> der Ring schließt sich, der Kreis setzt weich auf, der Haken zeichnet sich.
 * - Das Theme nimmt .btn--loading ohne Erfolg wieder weg (Fehler)
 *   -> der Button faltet sich ohne Haken zurück, die Meldung des Themes steht darunter wie bisher.
 * - Die Seitenleiste des Warenkorbs schließt (#CartDrawer verliert drawer--is-open)
 *   -> der Button faltet sich auf, mit Haken vor der Schrift, bis weitergescrollt oder woanders getippt wird.
 *
 * Nie: preventDefault, stopPropagation, Pointer-Capture, Eingriffe in Formular, Links, Ereignisse,
 * Seitenleiste oder deren Zeitpunkt. Alle Listener sind passiv. Jeder Fehler hier bleibt hier
 * (try/catch), das Theme läuft immer unverändert weiter.
 *
 * Bewegung: Reaktion beim Drücken (110 ms hin, 240 ms zurück), sonst weiche Kurven ohne Zurückschwingen
 * (weicher Impuls statt Feder, Moritz: "smooth, nicht wackelig").
 * Bei "Bewegung reduzieren" passiert nichts, der Button verhält sich genau wie im Theme.
 */
(() => {
  'use strict';

  const SEL_BTN = '[data-add-to-cart]';
  const SEL_TEXT = '[data-add-to-cart-text]';
  const NS = 'http://www.w3.org/2000/svg';

  /* Zeiten in ms */
  const T = {
    pressIn: 110, pressOut: 240,       /* Drücken, Loslassen */
    fold: 430,                          /* Button zum Kreis */
    ringClose: 120,                     /* Bogen schließt sich zum Ring */
    sink: 80,                           /* Kreis sinkt */
    tick: 160,                          /* Haken zeichnet sich */
    rise: 320,                          /* Kreis hebt sich zurück */
    unfold: 480,                        /* Kreis zum Button */
    textIn: 280,                        /* Schrift blendet ein */
    doneOut: 240,                       /* Haken vor der Schrift geht */
    giveUp: 15000,                      /* Antwortet das Theme so lange nicht, zurück zum normalen Button */
    drawerWait: 600,                    /* nach dem Aufsetzen: ist die Seitenleiste offen? */
    holdMax: 1000,                      /* so lange hält die Seitenleiste höchstens, bis der Haken steht (normal rund 520) */
    closingWait: 520                    /* Schließt die Seitenleiste des Themes (500), bleibt die Abdunklung so lange unsichtbar */
  };

  let reduce;
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)'); } catch (e) { return; }
  const supported = () => {
    try {
      if (!(window.CSS && CSS.supports)) return false;
      /* token-ok: Beispielwerte, nur um zu fragen, ob der Browser die Eigenschaften kann */
      const ok1 = CSS.supports('clip-path', 'inset(0px 1px round 2px)'); // token-ok: Prüfwert für die Browserfähigkeit
      const ok2 = CSS.supports('translate', '0px 1px'); // token-ok: Prüfwert für die Browserfähigkeit
      return !!(ok1 && ok2 && CSS.supports('scale', '1'));
    } catch (e) { return false; }
  };
  if (!supported()) return;

  /* ---------- Werkzeuge ---------- */
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  function bezier(x1, y1, x2, y2) {
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx, cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const sx = (t) => ((ax * t + bx) * t + cx) * t, sy = (t) => ((ay * t + by) * t + cy) * t, dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
    return (x) => {
      if (x <= 0) return 0; if (x >= 1) return 1;
      let t = x;
      for (let i = 0; i < 8; i++) { const e = sx(t) - x, d = dx(t); if (Math.abs(e) < 1e-5 || Math.abs(d) < 1e-6) break; t -= e / d; }
      return sy(clamp(t, 0, 1));
    };
  }
  const eReact = bezier(0.165, 0.84, 0.44, 1);   /* Kurve des Themes, wie --zl-ease-react */
  const eInOut = bezier(0.45, 0, 0.2, 1);
  const eFold = bezier(0.32, 0, 0.18, 1);
  const eSink = bezier(0.45, 0, 0.75, 0.6);
  const eDrawA = bezier(0.4, 0, 0.6, 1);
  const eDrawB = bezier(0.3, 0, 0.25, 1);

  /* Animation über requestAnimationFrame. Jede läuft auf einem Kanal (owner); ein neuer Start auf
     demselben Kanal beendet die alte, ohne dass sie noch etwas setzt. */
  function tween(owner, ms, fn, ease) {
    return new Promise((res) => {
      const g = ++owner.gen;
      if (ms <= 0) { fn(1); res(); return; }
      const t0 = performance.now();
      const step = (now) => {
        if (owner.gen !== g) return res();
        const k = clamp((now - t0) / ms, 0, 1);
        try { fn((ease || eReact)(k)); } catch (e) { return res(); }
        if (k < 1) requestAnimationFrame(step); else res();
      };
      requestAnimationFrame(step);
    });
  }
  /* Weicher Impuls: f(t) = (t/τ)·e^(1 − t/τ). Höchstwert einmal bei τ, klingt glatt aus, kein Zurückschwingen. */
  function pulse(owner, el, ax, ay, tau) {
    return new Promise((res) => {
      const g = ++owner.gen, t0 = performance.now();
      const step = (now) => {
        if (owner.gen !== g) return res();
        const u = (now - t0) / tau, f = u * Math.exp(1 - u);
        if (u > 7) { el.style.scale = ''; return res(); }
        el.style.scale = (1 + ax * f).toFixed(4) + ' ' + (1 + ay * f).toFixed(4);
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  /* ---------- Je Button ---------- */
  const states = new WeakMap();

  function svgEl(name, attrs) {
    const el = document.createElementNS(NS, name);
    for (const k in attrs) el.setAttribute(k, attrs[k]);
    return el;
  }

  function state(btn) {
    let s = states.get(btn);
    if (s) return s;
    s = {
      btn, phase: 'idle', success: false, k: 0, W: 0, H: 0, r0: 0,
      press: { gen: 0 }, lift: { gen: 0 }, morph: { gen: 0 }, pul: { gen: 0 }, ring: { gen: 0 }, draw: { gen: 0 }, done: { gen: 0 },
      spin: null, timers: [], drawerObs: null, ringSvg: null, arc: null, chk: null, haken: null, hakenPath: null
    };
    states.set(btn, s);
    return s;
  }

  function text(s) { return s.btn.querySelector(SEL_TEXT); }

  function later(s, ms, fn) { const id = setTimeout(() => { try { fn(); } catch (e) { hardReset(s); } }, ms); s.timers.push(id); return id; }
  function clearTimers(s) { s.timers.forEach(clearTimeout); s.timers = []; }

  function ensureRing(s) {
    if (s.ringSvg && s.ringSvg.isConnected) return;
    const svg = svgEl('svg', { viewBox: '0 0 52 52', 'aria-hidden': 'true', focusable: 'false', class: 'zl-kauf-ring' });
    const arc = svgEl('circle', { cx: '26', cy: '26', r: '16', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round' }); // token-ok: Bestand Kaufen-Button, Umstellung auf die Symbol-Regeln offen (docs/symbole.md)
    const chk = svgEl('path', { d: 'M19 26.2 L23.8 31 L33 20.8', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }); // token-ok: Bestand Kaufen-Button, Umstellung auf die Symbol-Regeln offen (docs/symbole.md)
    svg.append(arc, chk);
    s.btn.append(svg);
    s.ringSvg = svg; s.arc = arc; s.chk = chk;
  }
  function ensureHaken(s) {
    if (s.haken && s.haken.isConnected) return;
    const svg = svgEl('svg', { viewBox: '0 0 14 12', width: '14', height: '12', 'aria-hidden': 'true', focusable: 'false', class: 'zl-kauf-haken' });
    const p = svgEl('path', { d: 'M1.5 6.3 L5.2 10 L12.5 1.8', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }); // token-ok: Bestand Kaufen-Button, Umstellung auf die Symbol-Regeln offen (docs/symbole.md)
    svg.append(p);
    s.btn.append(svg);
    s.haken = svg; s.hakenPath = p;
  }

  /* Haken wie von Hand, der kurze Strich etwas langsamer, der lange schnell hinterher */
  function hideTick(path) { if (!path) return; const L = path.getTotalLength(); path.style.strokeDasharray = '0 ' + (L + 2); path.style.opacity = '0'; }
  async function drawTick(owner, path, ms, l1) {
    const L = path.getTotalLength();
    const set = (d) => { path.style.strokeDasharray = d.toFixed(2) + ' ' + (L + 2).toFixed(2); path.style.strokeDashoffset = '0'; path.style.opacity = d > 0.3 ? '1' : '0'; };
    set(0);
    await tween(owner, ms * 0.42, (k) => set(l1 * k), eDrawA);
    await tween(owner, ms * 0.58, (k) => set(l1 + (L - l1) * k), eDrawB);
  }

  /* Ladebogen: dreht ruhig, wird länger und kürzer */
  function startSpin(s) {
    const C = 2 * Math.PI * 16;
    const sp = { on: true, rot: 0, len: C * 0.17, ts: 0, last: performance.now(), C };
    const set = () => {
      s.arc.setAttribute('stroke-dasharray', sp.len.toFixed(2) + ' ' + (C + 1).toFixed(2));
      s.arc.setAttribute('transform', 'rotate(' + (sp.rot - 90).toFixed(2) + ' 26 26)');
    };
    const frame = (now) => {
      if (!sp.on) return;
      const dt = Math.min(0.05, (now - sp.last) / 1000); sp.last = now; sp.ts += dt;
      const ph = sp.ts * 2 * Math.PI / 1.6;
      sp.rot = (sp.rot + (230 + 100 * Math.sin(ph)) * dt) % 360;
      sp.len = C * (0.17 + 0.52 * (0.5 - 0.5 * Math.cos(ph)));
      set(); requestAnimationFrame(frame);
    };
    sp.set = set; set(); requestAnimationFrame(frame);
    s.spin = sp;
  }
  function closeSpin(s, ms) {
    const sp = s.spin; if (!sp) return Promise.resolve();
    sp.on = false;
    const l0 = sp.len, r0 = sp.rot;
    return tween(s.ring, ms, (k) => { sp.len = lerp(l0, sp.C, k); sp.rot = r0 + 70 * k; sp.set(); }, eInOut);
  }

  /* Form des Buttons: k = 0 Button, k = 1 Kreis mit dem Durchmesser der Buttonhöhe */
  function measure(s) {
    const b = s.btn;
    s.W = b.offsetWidth; s.H = b.offsetHeight;
    s.r0 = parseFloat(getComputedStyle(b).borderTopLeftRadius) || 0;
  }
  function apply(s, holdText) {
    const W = s.btn.offsetWidth || s.W, H = s.H;
    const X = clamp(s.k, 0, 1) * (W - H) / 2, w = W - 2 * X;
    const R = H / 2 - (H / 2 - Math.min(s.r0, H / 2)) * clamp((w - H) / (150 - H), 0, 1);
    s.btn.style.clipPath = s.k <= 0 ? '' : 'inset(0px ' + X.toFixed(2) + 'px round ' + R.toFixed(2) + 'px)';
    const t = text(s);
    if (t && !holdText) t.style.opacity = s.k <= 0 ? '' : eInOut(clamp((w - 100) / (290 - 100), 0, 1)).toFixed(3);
    if (s.ringSvg) s.ringSvg.style.opacity = eInOut(clamp((110 - w) / (110 - H), 0, 1)).toFixed(3);
  }

  function visible(btn) {
    if (!btn.isConnected || btn.disabled) return false;
    const r = btn.getBoundingClientRect();
    return r.width > 120 && r.height > 20 && getComputedStyle(btn).display !== 'none';
  }

  /* zl-kaufen.css lädt, ohne die Seite aufzuhalten. Erst wenn sie aktiv ist, darf das Skript etwas in den
     Button setzen, sonst könnte sich sein Layout verschieben. Bis dahin bleibt der Button wie im Theme. */
  const cssReady = () => getComputedStyle(document.documentElement).getPropertyValue('--kauf-css').trim() === '1';

  /* ---------- Ablauf ---------- */
  function onLoadingStart(btn) {
    if (reduce.matches || !cssReady()) return;
    const s = state(btn);
    if (s.phase === 'loading' || s.phase === 'landing') return;
    clearDone(s, true);
    if (!visible(btn)) return;
    clearTimers(s); stopDrawerObs(s);
    /* Reste eines noch laufenden Aufklappens (erneuter Tipp in genau diesem Moment) */
    s.done.gen++; s.draw.gen++;
    { const t0 = text(s); if (t0) t0.style.translate = ''; }
    if (s.haken) s.haken.style.opacity = '0';
    s.phase = 'loading'; s.success = false;
    btn.classList.remove('zl-kauf-fertig');
    btn.classList.add('zl-kauf');
    measure(s);
    ensureRing(s);
    s.ringSvg.style.width = s.H + 'px'; s.ringSvg.style.height = s.H + 'px';
    hideTick(s.chk);
    startSpin(s);
    const k0 = s.k;
    tween(s.morph, T.fold, (v) => { s.k = lerp(k0, 1, v); apply(s); }, eFold);
    /* Ankommen als Kreis: kaum spürbar voller, setzt sich */
    later(s, T.fold * 0.82, () => { if (s.phase === 'loading') pulse(s.pul, btn, 0.025, 0.025, 85); });
    /* Antwortet das Theme nicht, zurück zum normalen Button. Das Theme zeigt dann seinen eigenen Ladezustand. */
    later(s, T.giveUp, () => { if (s.phase === 'loading') hardReset(s); });
  }

  /* ---------- Seitenleiste, bis der Haken steht ----------
     Moritz, 04.10.2026: Die Seitenleiste kommt, wenn der Haken im Kreis steht, nicht schon davor.
     Nur Darstellung: Das Theme öffnet die Seitenleiste wie immer im selben Moment (Klassen, Fokus,
     Ereignisse, Warenkorb laden, ein Klick daneben schließt sie). Bis der Haken gezeichnet ist, hält
     zl-kaufen.css sie nur optisch am Startpunkt und die Abdunklung durchsichtig. Spätestens nach
     T.holdMax ist sie in jedem Fall frei, auch wenn hier etwas schiefgeht. */
  const hold = { on: false, cap: 0, end: 0 };
  function holdDrawer() {
    if (!document.getElementById('CartDrawer')) return;
    clearTimeout(hold.cap); clearTimeout(hold.end);
    hold.on = true;
    document.documentElement.classList.add('zl-kauf-halt');
    hold.cap = setTimeout(releaseDrawer, T.holdMax);
  }
  function releaseDrawer() {
    if (!hold.on) return;
    hold.on = false;
    clearTimeout(hold.cap);
    const html = document.documentElement;
    /* In der Zeit schon wieder geschlossen: auch das Ausblenden der Abdunklung bleibt unsichtbar */
    if (html.classList.contains('js-drawer-closing')) {
      hold.end = setTimeout(() => { if (!hold.on) html.classList.remove('zl-kauf-halt'); }, T.closingWait);
      return;
    }
    html.classList.remove('zl-kauf-halt');
  }

  async function onSuccess(btn) {
    try { await landing(btn); } finally { releaseDrawer(); }
  }
  async function landing(btn) {
    const s = states.get(btn);
    if (!s || s.phase !== 'loading') return;
    s.success = true;
    s.phase = 'landing';
    /* Zu Ende falten, falls die Antwort schneller war als die Bewegung */
    if (s.k < 1) { const k0 = s.k; tween(s.morph, 160, (v) => { s.k = lerp(k0, 1, v); apply(s); }, eReact); }
    await closeSpin(s, T.ringClose);
    if (s.phase !== 'landing') return;
    /* Sinkt ein kleines Stück, setzt weich auf, hebt sich zurück, dabei zeichnet sich der Haken */
    await tween(s.lift, T.sink, (v) => { btn.style.translate = '0px ' + (2.5 * v).toFixed(2) + 'px'; }, eSink);
    if (s.phase !== 'landing') return;
    const tk = drawTick(s.draw, s.chk, T.tick * 2, 6.8).catch(() => {}).then(releaseDrawer); /* Haken steht: Seitenleiste kommt */
    const back = tween(s.lift, T.rise, (v) => { btn.style.translate = '0px ' + (2.5 * (1 - v)).toFixed(2) + 'px'; }, eInOut);
    await pulse(s.pul, btn, 0.045, -0.04, 75);
    await Promise.all([tk, back]);
    if (s.phase !== 'landing') return;
    btn.style.translate = '';
    s.phase = 'waiting';
    later(s, Math.max(0, T.drawerWait - 450), () => waitForDrawer(s));
  }

  /* Ist die Seitenleiste offen, faltet sich der Button erst auf, wenn sie schließt. Sonst gleich. */
  function waitForDrawer(s) {
    if (s.phase !== 'waiting') return;
    const drawer = document.getElementById('CartDrawer');
    const open = drawer && drawer.classList.contains('drawer--is-open');
    if (!open) { unfold(s, true); return; }
    s.drawerObs = new MutationObserver(() => {
      if (!drawer.classList.contains('drawer--is-open')) { stopDrawerObs(s); later(s, 200, () => unfold(s, true)); }
    });
    s.drawerObs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }
  function stopDrawerObs(s) { if (s.drawerObs) { s.drawerObs.disconnect(); s.drawerObs = null; } }

  function onLoadingEnd(btn) {
    const s = states.get(btn);
    if (!s || s.phase !== 'loading') return;
    if (s.success) return;
    /* Kein Erfolg gemeldet: Fehler. Ohne Haken zurück, die Meldung des Themes steht darunter. */
    unfold(s, false);
  }

  async function unfold(s, withCheck) {
    const btn = s.btn;
    if (s.phase === 'idle' || s.phase === 'unfolding') return;
    stopDrawerObs(s);
    clearTimers(s);
    if (s.spin) s.spin.on = false;
    s.phase = 'unfolding';
    s.lift.gen++; btn.style.translate = '';
    const t = text(s);
    if (s.ringSvg) { const o0 = parseFloat(s.ringSvg.style.opacity || '0'); tween(s.ring, 220, (v) => { s.ringSvg.style.opacity = (o0 * (1 - v)).toFixed(3); }, eInOut); }
    if (t) t.style.opacity = '0';
    if (withCheck) placeHaken(s);
    later(s, T.unfold * 0.75, () => {
      if (t) tween(s.done, T.textIn, (v) => { t.style.opacity = v.toFixed(3); if (s.haken) s.haken.style.opacity = v.toFixed(3); }, eInOut)
        .then(() => { if (t) t.style.opacity = ''; });
    });
    const k0 = s.k;
    await tween(s.morph, T.unfold, (v) => { s.k = lerp(k0, 0, v); apply(s, true); }, eReact);
    if (s.phase !== 'unfolding') return;
    s.k = 0; btn.style.clipPath = ''; btn.style.scale = '';
    if (s.ringSvg) s.ringSvg.style.opacity = '0';
    if (withCheck) {
      s.phase = 'done';
      btn.classList.add('zl-kauf-fertig'); /* ab hier darf der Mouse-over wieder greifen (zl-kaufen.css) */
      armDoneReset(s);
    } else {
      s.phase = 'idle';
      if (t) { t.style.opacity = ''; t.style.translate = ''; }
      btn.classList.remove('zl-kauf');
    }
  }

  /* Haken vor der Schrift: die Schrift rückt um eine halbe Hakenbreite, der Haken steht davor */
  function placeHaken(s) {
    const t = text(s); if (!t) return;
    ensureHaken(s);
    const b = s.btn.getBoundingClientRect(), r = t.getBoundingClientRect();
    if (!r.width) return;
    const shift = 10;
    s.haken.style.left = (r.left - b.left - s.btn.clientLeft + shift - 20).toFixed(2) + 'px';
    s.haken.style.top = (r.top - b.top - s.btn.clientTop + r.height / 2 - 6).toFixed(2) + 'px';
    s.haken.style.opacity = '0';
    t.style.translate = shift + 'px 0px';
    hideTick(s.hakenPath);
    later(s, T.unfold * 0.75 + 60, () => { drawTick(s.draw, s.hakenPath, 300, 5.2).catch(() => {}); });
  }

  /* Wiedersehen endet beim Weiterscrollen oder wenn woanders hingetippt wird */
  function armDoneReset(s) {
    const y0 = window.scrollY;
    const onScroll = () => { if (Math.abs(window.scrollY - y0) > 60) clearDone(s, false); };
    const onDown = (e) => { if (!s.btn.contains(e.target)) clearDone(s, false); };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('pointerdown', onDown, { passive: true });
    s.unarm = () => { window.removeEventListener('scroll', onScroll); document.removeEventListener('pointerdown', onDown); s.unarm = null; };
  }
  function clearDone(s, instant) {
    if (s.phase !== 'done') return;
    if (s.unarm) s.unarm();
    s.phase = 'idle';
    const t = text(s), h = s.haken;
    const end = () => {
      if (t) { t.style.translate = ''; t.style.opacity = ''; }
      if (h) { h.style.opacity = '0'; hideTick(s.hakenPath); }
      if (s.phase === 'idle') s.btn.classList.remove('zl-kauf', 'zl-kauf-fertig');
    };
    if (instant) { s.done.gen++; end(); return; }
    tween(s.done, T.doneOut, (v) => {
      if (h) h.style.opacity = (1 - v).toFixed(3);
      if (t) t.style.translate = (10 * (1 - v)).toFixed(2) + 'px 0px';
    }, eInOut).then(() => { if (s.phase === 'idle') end(); });
  }

  /* Alles sofort zurück auf den Ausgangszustand des Themes */
  function hardReset(s) {
    clearTimers(s); stopDrawerObs(s);
    if (s.unarm) s.unarm();
    if (s.spin) s.spin.on = false;
    ['press', 'lift', 'morph', 'pul', 'ring', 'draw', 'done'].forEach((c) => { s[c].gen++; });
    const b = s.btn, t = text(s);
    s.phase = 'idle'; s.k = 0; s.success = false;
    b.style.clipPath = ''; b.style.scale = ''; b.style.translate = '';
    if (t) { t.style.opacity = ''; t.style.translate = ''; }
    if (s.ringSvg) s.ringSvg.style.opacity = '0';
    if (s.haken) s.haken.style.opacity = '0';
    b.classList.remove('zl-kauf', 'zl-kauf-press', 'zl-kauf-fertig'); clearPress(b);
    releaseDrawer();
  }

  /* ---------- Drücken: kurze Antwort unter dem Finger ----------
     Während der Button gedrückt kleiner ist, hält ein unsichtbarer Rand (::before, nur in dieser Zeit)
     die Klickfläche so groß wie vorher. So geht kein Tipp am Rand verloren. */
  /* Verkleinern und die unsichtbare Klickfläche (::before) genau so viel vergrößern, dass sie mit der
     Verkleinerung zusammen exakt die Fläche des Buttons vorher abdeckt, samt Rahmen und Eckenrundung.
     ::before liegt an der Innenkante (innerhalb des Rahmens), deshalb kommt die Rahmenbreite dazu. */
  function setPressScale(btn, sc) {
    const W = btn.offsetWidth, H = btn.offsetHeight;
    const r = parseFloat(getComputedStyle(btn).borderTopLeftRadius) || 0;
    btn.style.scale = sc.toFixed(4);
    btn.style.setProperty('--kauf-grow-x', (W / 2 * (1 / sc - 1) + btn.clientLeft).toFixed(3) + 'px');
    btn.style.setProperty('--kauf-grow-y', (H / 2 * (1 / sc - 1) + btn.clientTop).toFixed(3) + 'px');
    btn.style.setProperty('--kauf-grow-r', (r / sc).toFixed(3) + 'px');
  }
  function clearPress(btn) { ['--kauf-grow-x', '--kauf-grow-y', '--kauf-grow-r'].forEach((n) => btn.style.removeProperty(n)); }
  function pressDown(btn) {
    if (reduce.matches || btn.disabled || !cssReady()) return;
    const s = state(btn);
    if (s.phase !== 'idle' && s.phase !== 'done') return;
    btn.classList.add('zl-kauf-press');
    const s0 = parseFloat(btn.style.scale || '1') || 1;
    tween(s.press, T.pressIn, (v) => { setPressScale(btn, lerp(s0, 0.97, v)); }, eReact);
    s.pressed = true;
  }
  function pressUp(btn) {
    const s = states.get(btn);
    if (!s || !s.pressed) return;
    s.pressed = false;
    const s0 = parseFloat(btn.style.scale || '1') || 1;
    tween(s.press, T.pressOut, (v) => { if (s.phase === 'idle' || s.phase === 'done' || s.phase === 'loading') setPressScale(btn, lerp(s0, 1, v)); }, eInOut)
      .then(() => { if (!s.pressed) { btn.classList.remove('zl-kauf-press'); clearPress(btn); if (s.phase === 'idle' || s.phase === 'done') btn.style.scale = ''; } });
  }

  /* ---------- Beobachten ---------- */
  /* Nur der gefüllte Kaufen-Button. Umriss-Buttons (btn--tertiary, etwa in hervorgehobenen Produkten
     mit Express-Kasse) bleiben, wie sie sind: als Kreis hätten sie keine sichtbare Fläche. */
  function isBuyButton(el) {
    return !!(el && el.nodeType === 1 && el.matches(SEL_BTN) && !el.classList.contains('btn--tertiary') &&
      el.closest('form[action*="/cart/add"]'));
  }

  function start() {
    if (!document.body || !/(^|\s)template-product(\s|$)/.test(document.body.className)) return;

    /* Ladezustand des Themes */
    const seen = new WeakMap();
    new MutationObserver((list) => {
      for (const m of list) {
        const el = m.target;
        if (!isBuyButton(el)) continue;
        if (el.disabled) { const sd = states.get(el); if (sd && sd.phase === 'done') clearDone(sd, true); }
        const now = el.classList.contains('btn--loading');
        if (seen.get(el) === now) continue;
        seen.set(el, now);
        try { now ? onLoadingStart(el) : onLoadingEnd(el); } catch (e) { const s = states.get(el); if (s) hardReset(s); }
      }
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });

    /* Drehen oder neue Breite: Kreis neu messen; Haken im Wiedersehen ohne Bewegung entfernen */
    let rz = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(rz);
      rz = requestAnimationFrame(() => {
        document.querySelectorAll(SEL_BTN).forEach((b) => {
          const s = states.get(b); if (!s) return;
          try {
            if (s.phase === 'loading' || s.phase === 'landing' || s.phase === 'waiting') { measure(s); apply(s); }
            else if (s.phase === 'done') clearDone(s, true);
          } catch (e) { hardReset(s); }
        });
      });
    }, { passive: true });

    /* Erfolg: noch bevor das Theme die Seitenleiste öffnet (Erfassungsphase), sie optisch zurückhalten.
       Nur wenn der Kreis an diesem Button wirklich läuft. Fehlermeldungen (ohne detail) öffnen wie bisher sofort. */
    document.addEventListener('ajaxProduct:added', (e) => {
      try {
        const b = e && e.detail && e.detail.addToCartBtn;
        const s = isBuyButton(b) ? states.get(b) : null;
        if (s && s.phase === 'loading' && !reduce.matches) holdDrawer();
      } catch (err) { releaseDrawer(); }
    }, true);

    /* Erfolg, so wie das Theme ihn meldet */
    document.addEventListener('ajaxProduct:added', (e) => {
      const b = e && e.detail && e.detail.addToCartBtn;
      if (!isBuyButton(b)) return;
      try { onSuccess(b).catch(() => { const s = states.get(b); if (s) hardReset(s); }); } catch (err) { const s = states.get(b); if (s) hardReset(s); }
    });

    /* Drücken */
    document.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      const b = e.target && e.target.closest ? e.target.closest(SEL_BTN) : null;
      if (isBuyButton(b)) { try { pressDown(b); } catch (err) { /* nur Darstellung */ } }
    }, { passive: true });
    const up = (e) => {
      const b = e.target && e.target.closest ? e.target.closest(SEL_BTN) : null;
      if (!isBuyButton(b)) return;
      if (e.type === 'pointerout' && e.relatedTarget && b.contains(e.relatedTarget)) return;
      try { pressUp(b); } catch (err) { /* nur Darstellung */ }
    };
    document.addEventListener('pointerup', up, { passive: true });
    document.addEventListener('pointercancel', up, { passive: true });
    document.addEventListener('pointerout', up, { passive: true });

    /* Zurück aus dem Browser-Verlauf: alles in den Ausgangszustand */
    window.addEventListener('pageshow', (e) => {
      releaseDrawer();
      if (!e.persisted) return;
      document.querySelectorAll(SEL_BTN).forEach((b) => { const s = states.get(b); if (s) hardReset(s); });
    });
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  } catch (e) { /* nur Darstellung */ }
})();
